import { OrgRole } from "@/lib/organizationService";
import { useStore } from "@/store";

export interface OrgRoleResult {
  /** The caller's role in their organization, or null if not resolved yet. */
  orgRole: OrgRole | null;
  /**
   * Whether the caller may change what's *inside* a domain: generation,
   * audits, imports, enable/disable toggles, page and policy CRUD, publishing
   * to a connected CMS. True for `editor`, `admin` and `owner`.
   *
   * This is the flag almost every write control wants. `isAdmin` is the
   * narrower one — reach for it only when the action reshapes the org or
   * touches an integration connection.
   *
   * The backend enforces this independently: those endpoints are gated on
   * `min_role=editor`, and the matching Firestore paths on `isOrgEditor()`.
   * This hook exists so viewers aren't shown controls that 403 when clicked.
   */
  canEdit: boolean;
  /**
   * Whether the caller may reshape the organization: managing members, adding
   * or removing domains (a domain is the scope unit), and
   * connecting or disconnecting integrations (that binds someone's OAuth grant
   * and picks the property the whole team's numbers come from). True for
   * `admin` and `owner`, not `editor` — an editor works *through* an
   * integration, never on it.
   *
   * Comparing to `"admin"` alone would hide these from the one member who
   * cannot be removed from the org.
   */
  isAdmin: boolean;
  /**
   * Whether the caller owns the organization. Narrower than `isAdmin`: it gates
   * only the actions that are irreversible for everyone else — transferring
   * ownership, deleting the org.
   */
  isOwner: boolean;
}

/**
 * Read the caller's organization role.
 *
 * Every flag defaults to false while the role is still resolving, so controls
 * stay hidden until membership is known rather than flashing in and
 * disappearing.
 */
export const useOrgRole = (): OrgRoleResult => {
  const orgRole = useStore((state) => state.orgRole);

  return {
    orgRole,
    canEdit: orgRole === "editor" || orgRole === "admin" || orgRole === "owner",
    isAdmin: orgRole === "admin" || orgRole === "owner",
    isOwner: orgRole === "owner",
  };
};
